import { Flex, Spinner, Text, VStack } from 'native-base';
import React, { ReactNode } from 'react';
import { Platform } from 'react-native';
import WalletManager, { WalletError, WalletErrorCode } from 'react-native-wallet-manager';
import { useMutation } from 'react-query';

import AddToAppleWalletButton from 'app/components/AddToAppleWalletButton';
import HTML from 'app/components/HTML';
import useAuth from 'app/hooks/useAuth';
import { useI18n } from 'app/providers/I18nProvider';
import Analytics, { Events } from 'app/services/Analytics';
import { getWalletPassUrl, WalletPassURLProps } from 'app/services/GuestCenterService';
import { EntitlementReservation, TicketCategory } from 'app/services/GuestCenterService.types';
import Logger from 'app/services/Logger';
import { QueryKeys } from 'app/services/QueryClient';
import { tID } from 'app/services/TestHelper';

export type AddReservationToWalletProps = {
  children?: ReactNode;
  reservation: EntitlementReservation;
  passBarcode: string;
  ticketCategory?: TicketCategory;
};

export default function AddReservationToWallet({
  reservation,
  passBarcode: barcode,
  ticketCategory,
}: AddReservationToWalletProps) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const { data, mutate, isLoading, isError, isSuccess } = useAddToWalletMutation();

  const addToWallet = () => {
    mutate({
      uid: user?.uid ?? '',
      walletType: 'apple',
      attractionKey: reservation.attractionKey,
      attractionOrderNumber: reservation.attractionOrderNumber,
      locator: reservation.entitlementKey,
      barcode,
      locale,
      categoryKey: ticketCategory?.categoryKey,
      optionKey: reservation.optionKey,
    });
    Analytics.trackEvent(Events.AttractionAddAppleWallet, {
      productId: reservation.productLineup.contentKey,
      attractionKey: reservation.attractionConfig.contentKey,
    });
  };

  if (!['ios'].includes(Platform.OS)) return null;
  const action = user ? (
    <AddToAppleWalletButton onPress={addToWallet} />
  ) : (
    <Text fontSize="sm">{t('add_to_wallet_unavailable')}</Text>
  );

  return (
    <VStack alignItems="center" space="2" testID={tID('AddReservationToWallet')}>
      <Flex alignItems="center" h="10" justifyContent="center" mx="auto">
        {isLoading ? <Spinner size="sm" /> : action}
      </Flex>
      {isSuccess && !!data && <Text fontSize="xs">{t('gen_success')}</Text>}
      {isError && <HTML color="error.500" fontSize="sm" html={`${t('app_error_unknown')}`} />}
    </VStack>
  );
}

// The user dismissed the sheet, or tapped again while a pass was still being added.
const IGNORED_ERROR_CODES: WalletErrorCode[] = ['USER_CANCELLED', 'ADD_PASS_IN_PROGRESS'];

function getWalletErrorCode(err: unknown): WalletErrorCode | undefined {
  return (err as Partial<WalletError> | null)?.code;
}

function useAddToWalletMutation() {
  return useMutation(QueryKeys.AddToWallet, async (props: WalletPassURLProps) => {
    const url = getWalletPassUrl(props);
    Logger.debug('[AddReservationToWallet] adding pass', { url });
    try {
      return await WalletManager.addPassFromUrl(url);
    } catch (err) {
      const code = getWalletErrorCode(err);
      if (code && IGNORED_ERROR_CODES.includes(code)) {
        Logger.debug('[AddReservationToWallet] pass not added', { code });
        return false;
      }
      // the pass is already in Apple Wallet, which is what the user wanted
      if (code === 'PASS_ALREADY_EXISTS') {
        Logger.debug('[AddReservationToWallet] pass already in wallet', { code });
        return true;
      }
      throw err;
    }
  });
}
